import React, { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer
} from 'recharts';

// Define TypeScript interfaces if you're using TS; otherwise drop them for JS.
interface Item {
  C001: string;
  C002: string;
  C003: string;
  C004: string;
  BC004: string;
  C005: string;
  C006: string;
  C007: string;
  C008: string;
  C009: string;
  C010: string;
  C011: string;
  C012: string;
  C013: string;
  C014: string;
  C015: string;
  C022: string;
}

interface Order {
  _id: string;
  C002: string;
  C003: string;
  C004: string;
  C005: string;
  C006: string;
  C008: string;
  C009: string;
  C010: string;
  C011: string;
  C012: string;
  C013: string;
  CFTM: string;
  items: Item[];
}

const BIPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    fetch('https://mongodbapi.dahaeinc.co.kr/Mongo/GetSuju')
      .then((res) => res.json())
      .then((data: Order[]) => {
        setOrders(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  // Aggregate metrics
  const totalOrders = orders.length;
  const totalItems = orders.reduce((sum, order) => sum + order.items.length, 0);
  const totalRevenue = orders.reduce(
    (sum, order) =>
      sum +
      order.items.reduce((acc, item) => acc + parseFloat(item.C010), 0),
    0
  );

  // Prepare data for chart: revenue per product code
  const revenueMap: Record<string, number> = {};
  orders.forEach((order) => {
    order.items.forEach((item) => {
      const key = item.C004;
      const rev = parseFloat(item.C010);
      revenueMap[key] = (revenueMap[key] || 0) + rev;
    });
  });

  const chartData = Object.entries(revenueMap).map(([name, value]) => ({ name, value }));

  return (
    <div className="p-8 bg-gray-100 min-h-screen">
      <h1 className="text-3xl font-bold mb-6">BI Dashboard</h1>

      {loading ? (
        <p>Loading data...</p>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-6 mb-10">
            <div className="p-6 bg-white shadow-lg rounded-lg">
              <h2 className="text-lg font-semibold">Total Orders</h2>
              <p className="text-4xl mt-2">{totalOrders}</p>
            </div>
            <div className="p-6 bg-white shadow-lg rounded-lg">
              <h2 className="text-lg font-semibold">Total Items</h2>
              <p className="text-4xl mt-2">{totalItems}</p>
            </div>
            <div className="p-6 bg-white shadow-lg rounded-lg">
              <h2 className="text-lg font-semibold">Total Revenue</h2>
              <p className="text-4xl mt-2">${totalRevenue.toFixed(2)}</p>
            </div>
          </div>

          {/* Revenue Bar Chart */}
          <div className="bg-white p-6 shadow-lg rounded-lg mb-10">
            <h2 className="text-xl font-semibold mb-4">Revenue by Product Code</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Orders Table */}
          <div className="bg-white p-6 shadow-lg rounded-lg">
            <h2 className="text-xl font-semibold mb-4">Orders Overview</h2>
            <table className="min-w-full bg-white">
              <thead>
                <tr>
                  <th className="py-2 px-4 border">Order ID</th>
                  <th className="py-2 px-4 border">Date</th>
                  <th className="py-2 px-4 border"># Items</th>
                  <th className="py-2 px-4 border">Order Revenue</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const orderRevenue = order.items.reduce((acc, item) => acc + parseFloat(item.C010), 0);
                  return (
                    <tr key={order._id}>
                      <td className="py-2 px-4 border">{order._id}</td>
                      <td className="py-2 px-4 border">{order.CFTM}</td>
                      <td className="py-2 px-4 border">{order.items.length}</td>
                      <td className="py-2 px-4 border">${orderRevenue.toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default BIPage;
